import React, { useEffect, useState, useCallback } from 'react';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Rocket, Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAIState } from '../../contexts/AIStateContext';

// "Go fully autonomous" master switch — one toggle (with a daily spend cap)
// that activates auto-reply, auto-book, instant callback, auto-prospect,
// full email/call autonomy, voice human-transfer and the daily digest together.
export const GoAutonomousCard = () => {
  const { planAiEnabled, aiStatusLoaded } = useAIState();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [capInput, setCapInput] = useState(25);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/autonomy-master');
      setState(res.data || {});
      const existingCap = res.data?.components?.daily_spend_cap_usd;
      if (existingCap && existingCap > 0) setCapInput(existingCap);
    } catch (e) { /* non-blocking */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    const cap = Math.max(1, parseFloat(capInput) || 25);
    setSaving(true);
    try {
      await api.post('/api/brain/autonomy-master', { enabled: true, daily_spend_cap_usd: cap });
      toast.success('🚀 Fully autonomous mode is ON');
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not enable autonomous mode');
    } finally { setSaving(false); }
  };

  const disable = async () => {
    setSaving(true);
    try {
      await api.post('/api/brain/autonomy-master', { enabled: false });
      toast.success('Autonomous mode turned off');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not disable autonomous mode');
    } finally { setSaving(false); }
  };

  const onToggle = (v) => {
    if (v) setConfirmOpen(true);
    else disable();
  };

  if (loading || !state) return null;
  // Manual Pro (no-AI plan): autonomous engine is not available — hide the card.
  if (aiStatusLoaded && planAiEnabled === false) return null;

  const fully = !!state.fully_autonomous;
  const readiness = state.readiness || [];
  const gaps = readiness.filter((r) => !r.ok);
  const executionMode = (state.execution_mode || 'SIMULATED').toUpperCase();
  const isSimulated = executionMode === 'SIMULATED';
  // The "trust illusion" case: user turned autonomous flags ON but execution_mode
  // is still SIMULATED so nothing ships. Surface this loudly so users know.
  const switchOnButSimulated = fully === false
    && gaps.some((g) => g.key === 'execution_mode')
    && !!(state.components?.auto_reply_enabled || state.components?.auto_prospect_enabled);

  return (
    <>
      <div
        data-testid="go-autonomous-card"
        className={`rounded-xl border px-4 py-3.5 transition-colors ${
          fully
            ? 'border-indigo-500/40 bg-gradient-to-br from-indigo-500/[0.08] to-fuchsia-500/[0.05]'
            : 'border-border/60 bg-card/40'
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`mt-0.5 p-2 rounded-lg ${fully ? 'bg-indigo-500/15' : 'bg-muted'}`}>
              <Rocket className={`h-4 w-4 ${fully ? 'text-indigo-400' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                Go fully autonomous
                {fully && (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-400 bg-indigo-500/15 px-1.5 py-0.5 rounded" data-testid="go-autonomous-active-badge">
                    Active · Live
                  </span>
                )}
                {!fully && switchOnButSimulated && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide text-amber-400 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                    title="Autonomous flags are ON, but execution_mode is SIMULATED — the Brain drafts but does not ship. Flip to LIVE below."
                    data-testid="go-autonomous-simulated-badge"
                  >
                    <AlertTriangle className="h-3 w-3" /> Simulated · not shipping
                  </span>
                )}
                {!fully && !switchOnButSimulated && isSimulated && (
                  <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded" data-testid="go-autonomous-mode-badge">
                    Simulated
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground max-w-xl">
                One switch arms the whole engine — auto-reply, auto-book, instant callbacks,
                continuous prospecting, voice human-transfer & the daily digest. Spend stays
                inside your daily cap.
              </p>
              {switchOnButSimulated && (
                <p className="text-[11px] text-amber-500 mt-1.5 flex items-start gap-1.5" data-testid="go-autonomous-illusion-warning">
                  <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>
                    Your flags say autonomous but the engine is in <strong>SIMULATED</strong> mode —
                    nothing is being sent to real leads. Turn the switch off and back on to flip to LIVE,
                    or upgrade your plan if you&apos;re on TRIAL.
                  </span>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {saving ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
            <Switch
              checked={fully}
              disabled={saving}
              onCheckedChange={onToggle}
              data-testid="go-autonomous-switch"
            />
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="go-autonomous-readiness">
            {gaps.map((g) => (
              <span
                key={g.key}
                title={g.hint}
                className="inline-flex items-center gap-1 text-[11px] text-amber-500 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full"
                data-testid={`go-autonomous-gap-${g.key}`}
              >
                <AlertTriangle className="h-3 w-3" /> {g.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="go-autonomous-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-400" /> Activate fully autonomous mode
            </DialogTitle>
            <DialogDescription>
              The Brain will prospect, reply, call and book meetings on its own. Set a daily
              spend cap so it can never overspend — autonomous spend hard-stops at this amount each day.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <label className="text-sm font-medium text-foreground">Daily AI spend cap (USD)</label>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">$</span>
              <Input
                type="number" min="1" step="1"
                value={capInput}
                onChange={(e) => setCapInput(e.target.value)}
                className="w-32"
                data-testid="go-autonomous-cap-input"
              />
              <span className="text-xs text-muted-foreground">/ day</span>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1 pt-1">
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Auto-reply to positive inbound (never opt-outs/objections)</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Auto-book meetings & place instant callbacks</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Continuous ICP prospecting + contact reveal</li>
              <li className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> Voice human-transfer on low confidence + daily digest</li>
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="go-autonomous-cancel">
              Cancel
            </Button>
            <Button onClick={enable} disabled={saving} data-testid="go-autonomous-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Rocket className="h-4 w-4 mr-2" />}
              Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GoAutonomousCard;
